import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import { formatFieldValue } from '@/lib/record-form/format';
import type { IFieldOption, IModuleField } from '@/features/module-builder/types';
import { RatingInput } from './rating-input';
import { RecordLookupSelect } from './record-lookup-select';
import { UserSelect } from './user-select';

export interface RecordFieldInputProps {
    field: IModuleField;
    value: unknown;
    onChange: (value: unknown) => void;
    error?: string;
    id?: string;
    disabled?: boolean;
}

/** Radix SelectItem values cannot be '' — sentinel mapped back to '' on change. */
const SELECT_NONE_VALUE = '__none__';

function asText(value: unknown): string {
    if (value === null || value === undefined) return '';
    return String(value);
}

function OptionDot({ color }: { color: string }) {
    return (
        <span
            aria-hidden="true"
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: color }}
        />
    );
}

function MultiSelectChips({
    options,
    selected,
    onToggle,
    disabled,
    emptyText,
}: {
    options: IFieldOption[];
    selected: string[];
    onToggle: (optionValue: string) => void;
    disabled?: boolean;
    emptyText: string;
}) {
    if (options.length === 0) {
        return <p className="py-1 text-xs text-text-tertiary">{emptyText}</p>;
    }
    return (
        <div className="flex flex-wrap gap-1.5" role="group">
            {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                    <button
                        key={option.value}
                        type="button"
                        disabled={disabled}
                        aria-pressed={isSelected}
                        onClick={() => onToggle(option.value)}
                        className={cn(
                            'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors',
                            isSelected
                                ? 'border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900 dark:text-primary-300'
                                : 'border-border-default bg-bg-surface text-text-secondary hover:border-border-strong hover:text-text-primary',
                            disabled && 'cursor-not-allowed opacity-50',
                        )}
                        style={
                            isSelected && option.color
                                ? {
                                      backgroundColor: `${option.color}20`,
                                      borderColor: option.color,
                                      color: option.color,
                                  }
                                : undefined
                        }
                    >
                        {option.color && <OptionDot color={option.color} />}
                        {option.label || option.value}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Controlled polymorphic input for one module field. Renders only the control
 * — label, help text, and error copy live in `RecordFormSections`.
 */
export function RecordFieldInput({
    field,
    value,
    onChange,
    error,
    id,
    disabled,
}: RecordFieldInputProps) {
    const { t } = useTranslation();
    const invalid = error ? true : undefined;
    const placeholder = field.placeholder ?? undefined;
    const options = field.options ?? [];

    switch (field.fieldType) {
        case 'TEXTAREA':
            return (
                <Textarea
                    id={id}
                    rows={3}
                    value={asText(value)}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );

        case 'EMAIL':
        case 'URL':
            return (
                <Input
                    id={id}
                    type={field.fieldType === 'EMAIL' ? 'email' : 'url'}
                    value={asText(value)}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );

        case 'PHONE':
            return (
                <Input
                    id={id}
                    type="tel"
                    value={asText(value)}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );

        case 'NUMBER':
        case 'PERCENTAGE':
            return (
                <Input
                    id={id}
                    type="number"
                    value={asText(value)}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );

        case 'CURRENCY':
            return (
                <Input
                    id={id}
                    type="number"
                    startIcon={
                        <span className="text-sm">{field.settings?.currencySymbol ?? '$'}</span>
                    }
                    value={asText(value)}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );

        case 'DATE':
        case 'DATETIME':
            return (
                <Input
                    id={id}
                    type={field.fieldType === 'DATE' ? 'date' : 'datetime-local'}
                    value={asText(value)}
                    onChange={(event) => onChange(event.target.value)}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );

        case 'BOOLEAN':
            return (
                <Switch
                    id={id}
                    checked={value === true}
                    onCheckedChange={(checked) => onChange(checked)}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );

        case 'SELECT':
            return (
                <Select
                    value={asText(value)}
                    onValueChange={(next) => onChange(next === SELECT_NONE_VALUE ? '' : next)}
                    disabled={disabled}
                >
                    <SelectTrigger id={id} className="w-full" aria-invalid={invalid}>
                        <SelectValue
                            placeholder={
                                placeholder ?? t('recordForm.selectPlaceholder', 'Select an option')
                            }
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={SELECT_NONE_VALUE}>
                            <span className="text-text-tertiary">
                                {t('recordForm.selectNone', 'None')}
                            </span>
                        </SelectItem>
                        {options
                            .filter((option) => option.value !== '')
                            .map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    <span className="flex items-center gap-2">
                                        {option.color && <OptionDot color={option.color} />}
                                        {option.label || option.value}
                                    </span>
                                </SelectItem>
                            ))}
                    </SelectContent>
                </Select>
            );

        case 'MULTI_SELECT':
            return (
                <MultiSelectChips
                    options={options}
                    selected={Array.isArray(value) ? value.map(String) : []}
                    onToggle={(optionValue) => {
                        const selected = Array.isArray(value) ? value.map(String) : [];
                        onChange(
                            selected.includes(optionValue)
                                ? selected.filter((item) => item !== optionValue)
                                : [...selected, optionValue],
                        );
                    }}
                    disabled={disabled}
                    emptyText={t('recordForm.noOptions', 'No options configured')}
                />
            );

        case 'RATING':
            return (
                <RatingInput
                    value={Number(value) || 0}
                    onChange={(rating) => onChange(rating)}
                    settings={field.settings}
                    disabled={disabled}
                />
            );

        case 'AUTO_INCREMENT':
            return (
                <Input
                    id={id}
                    value={formatFieldValue(field, value)}
                    placeholder={t('recordForm.autoNumber', 'Generated automatically')}
                    disabled
                    readOnly
                />
            );

        case 'TIMEZONE':
            return (
                <TimezoneSelect
                    value={asText(value) || undefined}
                    onChange={(timezone) => onChange(timezone)}
                    placeholder={placeholder}
                    disabled={disabled}
                    invalid={!!error}
                />
            );

        case 'LOOKUP':
            return (
                <RecordLookupSelect
                    field={field}
                    value={asText(value)}
                    onChange={(recordId) => onChange(recordId)}
                    disabled={disabled}
                />
            );

        case 'USER':
            return (
                <UserSelect
                    value={asText(value)}
                    onChange={(userId) => onChange(userId)}
                    disabled={disabled}
                />
            );

        case 'TEXT':
        default:
            return (
                <Input
                    id={id}
                    value={asText(value)}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={invalid}
                />
            );
    }
}
